import { IProjectSchema, projectSchema, ProjectService } from '@/api/project';
import { InputField } from '@/components/form/fields/InputField';
import { FormContainer, FormFooter } from '@/components/form/FormContainer';
import { Loading } from '@/components/Loading';
import { Section } from '@/components/Section';
import { OutlineButton } from '@/components/shared/button/OutlineButton';
import { useBackNavigate } from '@/utils/hooks/useBackNavigate';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Dialog } from 'alurkerja-ui';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

export const CreateEditProject: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const backNavigate = useBackNavigate();

  const { data: projectData, isFetching: isProjectLoading } =
    ProjectService.useGet(id);
  const isFetchLoading = isProjectLoading;

  const form = useForm<IProjectSchema>({
    resolver: zodResolver(projectSchema),
  });

  const { mutate: create, isPending: isLoading } = ProjectService.useCreate();
  const { mutate: update, isPending: isLoadingUpdate } =
    ProjectService.useUpdate();

  const submitAction = (data: IProjectSchema) => {
    if (id) {
      data.id = id;

      if (!data.title.trim()) {
        form.setError('title', {
          type: 'required',
          message: 'Title is required',
        });
        return;
      }

      update(data, {
        onSuccess: () => {
          Dialog.success({
            title: 'Berhasil',
            description: 'Proyek Berhasil Diubah.',
            callback: () => navigate('/project', { replace: true }),
          });
        },
        onError: (error: any) => {
          Dialog.error({
            title: 'Gagal',
            description: error.response.data.message,
          });
        },
      });
    } else {
      create(data, {
        onSuccess: () => {
          Dialog.success({
            title: 'Berhasil',
            description: 'Proyek Berhasil Dibuat.',
            callback: () => navigate('/project', { replace: true }),
          });
        },
        onError: (error: any) => {
          Dialog.error({
            title: 'Gagal',
            description: error.response.data.message,
          });
        },
      });
    }
  };

  return (
    <Section title={id ? 'Update Project' : 'Create Project'}>
      {isFetchLoading ? (
        <Loading />
      ) : (
        <FormContainer form={form} onSubmit={submitAction}>
          <div className="flex flex-col gap-4">
            <InputField
              name="title"
              label="Project Name"
              placeholder="Nama Project"
              hasRequiredAsterisk
              defaultValue={projectData?.title}
            />
          </div>

          <FormFooter>
            <OutlineButton onClick={backNavigate}>Back</OutlineButton>
            <Button type="submit" loading={isLoading || isLoadingUpdate}>
              {id ? 'Update' : 'Create'}
            </Button>
          </FormFooter>
        </FormContainer>
      )}
    </Section>
  );
};
